import type { EditablePath, EditorNode, EditorPoint } from './path-editor';

export type EditorChromeBounds = { left: number; top: number; right: number; bottom: number };
export type EditorChromeHit = { node: string; handle?: 'inHandle' | 'outHandle' };
export type PathEditorChrome = {
  anchors: string; selectedAnchors: string; anchorHits: string;
  handleLines: string; handles: string; handleHits: string;
  nodes: { node: EditorNode; selected: boolean }[];
  unit: number; handleCount: number;
};

type Marker = { unit: number; anchor: string; anchorHit: string; handleLines?: string; handles?: string; handleHits?: string };
// Immutable editor nodes keep the same marker strings during pan and selection.
// Only the latest scale is retained, so repeated zooms do not accumulate caches.
const markers = new WeakMap<EditorNode, Marker>();
const rectangle = (p: EditorPoint, r: number) => `M${p.x - r} ${p.y - r}h${2 * r}v${2 * r}h${-2 * r}Z`;
const circle = (p: EditorPoint, r: number) => `M${p.x + r} ${p.y}a${r} ${r} 0 1 0 ${-2 * r} 0a${r} ${r} 0 1 0 ${2 * r} 0Z`;
function marker(node: EditorNode, unit: number, handles: boolean) {
  let value = markers.get(node);
  if (!value || value.unit !== unit) {
    value = { unit, anchor: rectangle(node.point, 3 * unit), anchorHit: rectangle(node.point, 6 * unit) };
    markers.set(node, value);
  }
  if (handles && value.handles === undefined) {
    let lines = '', visible = '', hits = '';
    for (const side of ['inHandle', 'outHandle'] as const) if (node[side]) {
      const p = node[side]!;
      lines += `M${node.point.x} ${node.point.y}L${p.x} ${p.y}`;
      visible += circle(p, 3 * unit); hits += circle(p, 8 * unit);
    }
    value.handleLines = lines; value.handles = visible; value.handleHits = hits;
  }
  return value;
}

/** Batch every visible actual anchor into six SVG paths; never resample or
 * decimate geometry. The same margin and selected-handle culling apply as in
 * the individual-node editor, including anchors just outside the viewport. */
export function buildPathEditorChrome(paths: readonly EditablePath[], linked: ReadonlySet<string>, unit: number, bounds: EditorChromeBounds): PathEditorChrome {
  if (!Number.isFinite(unit) || unit <= 0) throw new Error('Invalid editor marker scale');
  const visible = (p?: EditorPoint) => Boolean(p && p.x >= bounds.left - 12 * unit && p.x <= bounds.right + 12 * unit && p.y >= bounds.top - 12 * unit && p.y <= bounds.bottom + 12 * unit);
  const anchors: string[] = [], selectedAnchors: string[] = [], anchorHits: string[] = [], handleLines: string[] = [], handles: string[] = [], handleHits: string[] = [];
  const nodes: PathEditorChrome['nodes'] = [];
  let handleCount = 0;
  for (const path of paths) for (const subpath of path.subpaths) for (const node of subpath.nodes) {
    const selected = linked.has(node.id);
    if (!visible(node.point) && !(selected && (visible(node.inHandle) || visible(node.outHandle)))) continue;
    nodes.push({ node, selected });
    const shape = marker(node, unit, selected);
    (selected ? selectedAnchors : anchors).push(shape.anchor); anchorHits.push(shape.anchorHit);
    if (selected) {
      handleLines.push(shape.handleLines!); handles.push(shape.handles!); handleHits.push(shape.handleHits!);
      if (node.inHandle) handleCount++; if (node.outHandle) handleCount++;
    }
  }
  return { anchors: anchors.join(''), selectedAnchors: selectedAnchors.join(''), anchorHits: anchorHits.join(''), handleLines: handleLines.join(''), handles: handles.join(''), handleHits: handleHits.join(''), nodes, unit, handleCount };
}

/** Resolve the same per-node painter priority as the former SVG groups:
 * later nodes win overlaps, anchors win their own coincident handles, and an
 * outgoing handle wins its coincident incoming handle. Tested in source units. */
export function hitPathEditorChrome(chrome: PathEditorChrome, point: EditorPoint): EditorChromeHit | null {
  const anchorRadius = 6 * chrome.unit, handleRadius2 = (8 * chrome.unit) ** 2;
  for (let i = chrome.nodes.length - 1; i >= 0; i--) {
    const { node, selected } = chrome.nodes[i];
    if (Math.abs(point.x - node.point.x) <= anchorRadius && Math.abs(point.y - node.point.y) <= anchorRadius) return { node: node.id };
    if (selected) for (const side of ['outHandle', 'inHandle'] as const) {
      const p = node[side];
      if (p && (point.x - p.x) ** 2 + (point.y - p.y) ** 2 <= handleRadius2) return { node: node.id, handle: side };
    }
  }
  return null;
}
